import React, { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import LinearProgress from "@mui/material/LinearProgress";
import { makeStyles } from "@mui/styles";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemButton from "@mui/material/ListItemButton";
import Checkbox from "@mui/material/Checkbox";
import Collapse from "@mui/material/Collapse";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import Button from "@mui/material/Button";
import RefreshButton from "../../core/RefreshButton";


const useStyles = makeStyles({
    panel: {
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        padding: "24px",
        backgroundColor: "#f7f7f7",
        marginBottom: "24px",
        maxHeight: "500px",
        overflowY: "scroll",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
    },
    processContainer: {
        display: "flex",
        flexDirection: "row",
        alignItems: "flex-start",
        width: "100%",
        marginBottom: "16px",
    },
    groupedProcessContainer: {
        display: "flex",
        flexDirection: "column",
        width: "100%",
        paddingLeft: "32px",
        marginBottom: "16px",
    },
    groupedProcessText: {
        fontWeight: "500",
        color: "#4A4A4A",
    },
    progressBar: {
        marginTop: "8px",
        borderRadius: "8px",
        backgroundColor: "#e0e0e0",
    },
    listItemButton: {
        borderRadius: "8px",
        margin: "8px 0",
        transition: "background-color 0.2s",
        "&:hover": {
            backgroundColor: "#e8e8e8",
        },
    },
});

function LinearProgressWithLabel(props) {
    return (
        <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box sx={{ width: "100%", mr: 1 }}>
                <LinearProgress variant="determinate" {...props} />
            </Box>
            <Box sx={{ minWidth: 35 }}>
                <Typography variant="body2" color="text.secondary">{`${
                    Math.round((props.value + Number.EPSILON) * 100) / 100
                }%`}</Typography>
            </Box>
        </Box>
    );
}

function formatTimeAgo(date) {
    const diffMs = Date.now() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return "just now";
    if (diffMin === 1) return "1 minute ago";
    if (diffMin < 60) return `${diffMin} minutes ago`;
    const diffHours = Math.floor(diffMin / 60);
    if (diffHours === 1) return "1 hour ago";
    return `${diffHours} hours ago`;
}

function ProcessDetails({ process, enableMultiSelect, isSelected, onToggleSelect, lastUpdated }) {
    const classes = useStyles();

    return (
        <Box className={classes.processContainer} display="flex" alignItems="flex-start">
            {enableMultiSelect && (
                <Checkbox
                    checked={isSelected}
                    onChange={() => onToggleSelect(process.process_run_id)}
                    sx={{ marginRight: 2, marginTop: 0, padding: 0 }}
                />
            )}
            <Box flex={1}>
                <Box display="flex" justifyContent="space-between">
                    <Typography variant="body1" className={classes.groupedProcessText}>
                        {process.process_run_id}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Owners: {process.owner_emails.join(", ")}
                    </Typography>
                </Box>
                {process.client_name && (
                    <Typography variant="body2" color="text.secondary">
                        Client: {process.client_name} | Project: {process.project_name}
                    </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                    Type: {process.process_type}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Group: {process.process_group}
                </Typography>
                <Box display="flex" justifyContent="space-between">
                    {process.message && (
                        <Typography variant="caption" color="text.secondary">
                            {process.message}
                        </Typography>
                    )}
                    <Box display="flex" flexDirection="column" alignItems="flex-end">
                        <Typography variant="caption" color="text.secondary">
                            Started at: {new Date(process.started_at).toUTCString()}
                        </Typography>
                        {lastUpdated && (
                            <Typography variant="caption" color="text.secondary">
                                Last updated {formatTimeAgo(lastUpdated)}: {lastUpdated.toUTCString()}
                            </Typography>
                        )}
                    </Box>
                </Box>
                <LinearProgressWithLabel
                    variant="determinate"
                    value={process.progress_percentage}
                    className={classes.progressBar}
                />
            </Box>
        </Box>
    );
}

function GroupedProcess({ processes, enableMultiSelect, selectedProcesses, onToggleSelect, lastUpdatedMap }) {
    const classes = useStyles();
    const [expanded, setExpanded] = useState(false);

    return (
        <div>
            <ListItemButton className={classes.listItemButton} onClick={() => setExpanded(!expanded)}>
                <ListItemText primary={processes[0].group} primaryTypographyProps={{ fontWeight: "600" }} />
                {expanded ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
                {processes.map((process) => (
                    <ListItem key={process.process_run_id} disablePadding>
                        <ProcessDetails
                            process={process}
                            enableMultiSelect={enableMultiSelect}
                            isSelected={selectedProcesses.includes(process.process_run_id)}
                            onToggleSelect={onToggleSelect}
                            lastUpdated={lastUpdatedMap[process.process_run_id]}
                        />
                    </ListItem>
                ))}
            </Collapse>
        </div>
    );
}

export default function RunningProcessesPanel({
    processes,
    refreshJobs,
    processType,
    isLoading,
    enableMultiSelect = true,
    selectedProcesses = [],
    setSelectedProcesses = () => {},
}) {
    const classes = useStyles();

    // Track when each process was last updated by comparing message/progress across polls
    const prevProcessesRef = useRef({});
    const [lastUpdatedMap, setLastUpdatedMap] = useState({});

    useEffect(() => {
        const now = new Date();
        const prev = prevProcessesRef.current;
        const updates = {};

        for (const process of processes) {
            const id = process.process_run_id;
            const prevProcess = prev[id];
            if (!prevProcess || prevProcess.message !== process.message || prevProcess.progress_percentage !== process.progress_percentage) {
                updates[id] = now;
            }
        }

        if (Object.keys(updates).length > 0) {
            setLastUpdatedMap((current) => ({ ...current, ...updates }));
        }

        const snapshot = {};
        for (const process of processes) {
            snapshot[process.process_run_id] = {
                message: process.message,
                progress_percentage: process.progress_percentage,
            };
        }
        prevProcessesRef.current = snapshot;
    }, [processes]);

    const handleToggleSelect = (processRunId) => {
        setSelectedProcesses((prev) => {
            if (prev.includes(processRunId)) {
                return prev.filter((id) => id !== processRunId);
            } else {
                return [...prev, processRunId];
            }
        });
    };

    // Organize processes into standalone and groups
    const standaloneProcesses = processes.filter((p) => !p.group);
    const groupedProcessesMap = processes
        .filter((p) => p.group)
        .reduce((groups, process) => {
            if (!groups[process.group]) {
                groups[process.group] = [];
            }
            groups[process.group].push(process);
            return groups;
        }, {});
    const numberOfRunningProcesses = standaloneProcesses.length + Object.keys(groupedProcessesMap).length;

    const allProcessIds = processes.map((p) => p.process_run_id);
    const allSelected = allProcessIds.length > 0 && allProcessIds.every((id) => selectedProcesses.includes(id));

    const handleSelectAll = () => {
        if (allSelected) {
            setSelectedProcesses([]);
        } else {
            setSelectedProcesses(allProcessIds);
        }
    };

    return (
        <>
            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                <Typography variant="h6" gutterBottom style={{ marginBottom: 0 }}>
                    Currently Running Processes ({numberOfRunningProcesses})
                    {enableMultiSelect && selectedProcesses.length > 0 && ` - ${selectedProcesses.length} selected`}
                </Typography>
                <RefreshButton
                    onClickFunction={refreshJobs}
                    setValue={(_) => {}}
                    valueName={null}
                    disabledBoolean={false}
                    ariaLabel={`refresh all processes`}
                    isLoading={isLoading}
                />
                {enableMultiSelect && processes.length > 0 && (
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={handleSelectAll}
                    >
                        {allSelected ? "Deselect All" : "Select All"}
                    </Button>
                )}
            </div>
            <div className={classes.panel}>
                <List component="nav" aria-label="running processes">
                    {standaloneProcesses.map((process) => (
                        <ListItem key={process.process_run_id}>
                            <ProcessDetails process={process}
                                enableMultiSelect={enableMultiSelect}
                                isSelected={selectedProcesses.includes(process.process_run_id)}
                                onToggleSelect={handleToggleSelect}
                                lastUpdated={lastUpdatedMap[process.process_run_id]}
                            />
                        </ListItem>
                    ))}
                    {Object.values(groupedProcessesMap).map((groupedProcesses) => (
                        <GroupedProcess
                            key={groupedProcesses[0].group}
                            processes={groupedProcesses}
                            enableMultiSelect={enableMultiSelect}
                            selectedProcesses={selectedProcesses}
                            onToggleSelect={handleToggleSelect}
                            lastUpdatedMap={lastUpdatedMap}
                        />
                    ))}
                </List>
            </div>
        </>
    );
}
